import type {Ref} from 'vue';
import {ref} from 'vue';
import {defineStore} from 'pinia';
import {useAsyncData, useDataTracking, useLogger, useMaltVisitorId, useResponseHeader, useUniversalFetch} from '#imports';

type Campaigns<T extends string[]> = Record<T[number], string>;

/**
 * Do not call this function directly, use the global store provided by useABTestStore()
 * See: lib-front-core/nuxt-features-module/src/runtime/composables/useABTestStore.ts
 *
 * This function will create a Pinia store that can fetch and infer types of your AB Test campaigns
 * It's designed to be used at app start and persist during app lifecycle
 */
export function createABTestStore<T extends string[] = []>(storeName = 'abtest-store') {
    return defineStore(storeName, () => {
        const campaigns = ref({}) as Ref<Campaigns<T>>;
        const logger = useLogger();
        const maltVisitorId = useMaltVisitorId();
        const {track} = useDataTracking();

        async function fetchCampaignsVariations() {
            if (!maltVisitorId.value) {
                logger.error('Trying to fetch campaigns variations without Malt visitor ID');
            }

            const {data, error} = await useAsyncData('abtest-campaigns-variations', () =>
                useUniversalFetch<Campaigns<T>>(`/public-api/experiment/campaigns?maltVisitorId=${maltVisitorId.value}`),
            );
            if (error.value) {
                logger.error(`Error while fetching campaigns variations for visitor ${maltVisitorId.value}`, error.value);
            }
            campaigns.value = data.value as Campaigns<T>;
        }

        function getVariation(campaignId: string): string | null {
            const variationId = getVariationWithoutTracking(campaignId);
            if (variationId) {
                /**
                 * Ensure we don't store this page in cache when an AB test campaign is running
                 * See `usePageCache` composable for more information
                 */
                const cacheControlHeader = useResponseHeader('Cache-Control');
                cacheControlHeader.value = `no-store`;

                // eslint-disable-next-line @malt/tracking-event-name
                track('$exposure', {flag_key: campaignId, variant: variationId});
            }
            return variationId;
        }

        function getVariationWithoutTracking(campaignId: string): string | null {
            if (!campaigns.value) {
                logger.error('Campaigns should not be null');
                return null;
            }
            return campaigns.value[campaignId as keyof typeof campaigns.value];
        }

        return {
            campaigns,
            fetchCampaignsVariations,
            getVariationWithoutTracking,
            getVariation,
        };
    });
}
